import React, { FC, useState, useEffect } from 'react'
import { useLocation, useParams, useHistory } from 'react-router'
import { Box, Text, Heading, Flex } from '@chakra-ui/core'
import { useRecoilValue, useRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { configState, orderConfigState } from '../../recoil/atoms'
import { isMobileOnly } from 'react-device-detect'
import { LinkButton } from '../../lib/types'
import { useQuery } from '@apollo/client'
import { Counter, ImageBox } from '@engyalo/storefront-components'
import { Price } from '../../components/Price'
import { Hook } from '@engyalo/storefront-types'
import { cartState } from '../../recoil/atoms'
import Plugin from '../../Plugin'
import { ICartDictionary, ICartStats, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { cartStatsState } from '../../recoil/selectors'
import { searchProductsByIdQuery } from '../../queries'
import {
  IProductsPricedPerCollectionVariables,
  IProductsPricedPerCollection,
  IProductPricedPerCollectionLevel1 as productLevel1Type,
} from '@engyalo/storefront-types/lib/graphql/product'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import { CartIcon } from '../../components/CartIcon'
import { getActionScreen } from '../../utils/analytics'
import { addProduct, removeProduct, updateQuantity } from '@engyalo/storefront-events'
import { ILocationSearch, skipIfInPreviewMode } from '../../utils/mode'

export const Product: FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<ILocationSearch>()
  const { customer, order } = useParams()
  const config = useRecoilValue(configState)
  const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
  const [canAddToCart, setCanAddToCart] = useState(true)
  const cartStats = useRecoilValue<ICartStats>(cartStatsState)
  const [productToAddToCart, setproductToAdd] = useState({} as IProduct)
  const [priceEnabled, setPriceEnabled] = useState(false)
  const [productData, setProductData] = useState({} as productLevel1Type)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [verticalHeight, setVerticalHeight] = useState(window.innerHeight * 0.01)

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  const {
    state: { product },
  } = useLocation()

  useEffect((): void => {
    setproductToAdd({
      ...product,
      cartId: generateCartId(product),
    })
  }, [product])

  const { loading: loadingProduct } = useQuery<IProductsPricedPerCollection, IProductsPricedPerCollectionVariables>(
    searchProductsByIdQuery,
    {
      variables: {
        name: customer,
        configId: order,
        productId: product.id,
      },
      skip: !customer,
      onCompleted(data: IProductsPricedPerCollection) {
        const currentCollection = data?.productsPricedPerCollection?.[0]

        setPriceEnabled(currentCollection?.enabledPrice || true)
        setProductData(currentCollection?.productsPriced?.[0] ?? productData)
      },
      returnPartialData: true,
      fetchPolicy: 'no-cache',
    }
  )

  const getProductFirstImage = (product: productLevel1Type): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const addToCart = (product: IProduct, quantity = 1, backToCatalogue = false, shouldReplace = false): void => {
    if (canAddToCart) {
      const tempCart = { ...cart }
      if (product && product.id) {
        const currentSku = generateCartId(product)
        if (tempCart[currentSku]) {
          const tempProd = { ...tempCart[currentSku] }
          if (tempProd.quantity) {
            const totalPrice = getTotalPrice(product)
            tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity

            if (tempProd.quantity > 0) {
              tempCart[currentSku] = {
                ...tempProd,
                totalPrice,
              }
              setCart(tempCart)
            } else {
              delete tempCart[currentSku]
              setCart({ ...tempCart })
            }
          }
        } else if (quantity > 0) {
          const totalPrice = getTotalPrice(product)

          tempCart[currentSku] = {
            ...product,
            totalPrice,
            quantity: quantity,
            cartId: currentSku,
          } as IProduct
          setCart(tempCart)
        }

        skipIfInPreviewMode(() => {
          if (shouldReplace) {
            updateQuantity({
              product: {
                id: product.id || '',
                sku: product.sku || '',
                name: product.name || '',
                category: product.category || '',
                price: product.price || 0,
                note: product.note || '',
                quantity,
                screen: getActionScreen(location),
              },
              session,
            })
          } else {
            if (quantity > 0) {
              addProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: product.note || '',
                  screen: getActionScreen(location),
                },
                session,
              })
            } else {
              removeProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: product.note || '',
                  screen: getActionScreen(location),
                },
                session,
              })
            }
          }
        })

        if (backToCatalogue) {
          history.push({
            pathname: `/${customer}/${order}`,
            search: location.search,
          })
        }
      }
    }
  }

  useEffect(() => {
    function handleResize() {
      const newVerticalHeight = window.innerHeight * 0.01
      setVerticalHeight(newVerticalHeight)
      console.log('resize')
    }

    window.addEventListener('resize', handleResize)
  })

  if (product) {
    return (
      <Box h={isMobileOnly ? `calc(var(--vh, 1${verticalHeight}) * 100)` : '100%'} w="100%" d="flex" flexDir="column">
        <Box
          h="4.0rem"
          d="flex"
          justifyContent="space-between"
          paddingX="1.2rem"
          top={0}
          flex="0 0 auto"
          position="sticky"
          background="white"
          maxW="42.5rem"
          width="100%"
          zIndex={2}
        >
          <LinkButton
            to={{ pathname: `/${customer}/${order}`, search: location.search }}
            h="100%"
            w="3.2rem"
            background="transparent"
            borderColor="transparent"
            size="lg"
            aria-label="cart"
            icon="chevron-left"
          />
          <CartIcon />
        </Box>
        {/* Body */}
        <Plugin
          hook={Hook.PRODUCT_DETAIL_BODY}
          pluginProps={{
            product: productData,
            isLoading: loadingProduct,
            cart,
            config,
            enablePrice: priceEnabled,
            setCanAddToCart,
            tFunction: t,
            setproductToAdd,
          }}
        >
          <>
            <Box paddingX="1.6rem" marginTop="4rem" height="100%" minH="90vh" overflowY="scroll">
              <ImageBox imageUrl={getProductFirstImage(productToAddToCart)} />
              <Heading marginY="4px" as="h3" fontSize="xl" flex="1">
                {productToAddToCart.name}
              </Heading>
              <Text fontSize="md" fontFamily="body" color="gray.100" paddingBottom="0.4rem">
                {productToAddToCart.description}
              </Text>
              <Price price={productToAddToCart.price} currency={config.currencyPrefix} />
            </Box>
            <Flex flex="1" flexDirection="column" overflow="auto" marginTop=".8rem" paddingX=".4rem" />
          </>
        </Plugin>
        {/* Body */}
        {/* Footer */}
        <Plugin
          hook={Hook.PRODUCT_ADD_TO_CART}
          pluginProps={{
            product: productToAddToCart,
            config,
            addToCart,
            cartStats,
            cart,
            tFunction: t,
            canAddToCart,
            orderConfig,
          }}
        >
          <Box
            zIndex={1}
            paddingY="1.2rem"
            paddingX="0.8rem"
            boxShadow="md"
            backgroundColor="white"
            position="sticky"
            bottom={0}
            flex="0 0 auto"
          >
            <Box w="50%" m="auto">
              <Counter
                name={productToAddToCart.cartId || ''}
                addAction={(event: React.MouseEvent<HTMLButtonElement>): void => {
                  event.stopPropagation()
                  addToCart(productToAddToCart, 1)
                }}
                minusAction={(event: React.MouseEvent<HTMLButtonElement>): void => {
                  event.stopPropagation()
                  addToCart(productToAddToCart, -1)
                }}
                onInputBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
                  const quantity = parseInt(event.target.value)
                  addToCart(productToAddToCart, quantity, false, true)
                }}
                value={cart[productToAddToCart.cartId || ''] ? cart[productToAddToCart.cartId || ''].quantity : 0}
              />
            </Box>
          </Box>
        </Plugin>
        {/* Footer */}
      </Box>
    )
  }
  return <div> no product</div>
}
